(() => {
  const SELECTOR = [
    'variant-picker select.variant-option__select',
    '.bbt-pm-configurator select',
    '.bbt-shockworks select',
    '.product-details select',
    'form[action*="/cart/add"] select'
  ].join(',');

  const ENHANCED = 'data-bbt-select-enhanced';
  let uid = 0;
  let syncQueued = false;

  const cssEscape = (value) => {
    if (window.CSS && typeof window.CSS.escape === 'function') return window.CSS.escape(value);
    return String(value).replace(/[^a-zA-Z0-9_-]/g, '\\$&');
  };

  function getLabel(select) {
    if (select.id) {
      const explicit = document.querySelector(`label[for="${cssEscape(select.id)}"]`);
      if (explicit?.textContent?.trim()) return explicit.textContent.trim();
    }
    const parentLabel = select.closest('label');
    if (parentLabel?.textContent?.trim()) return parentLabel.textContent.trim();
    const field = select.closest('.bbt-pm-configurator__field, .variant-option, .product-form__input, .field');
    const candidate = field?.querySelector('label, .form__label, .variant-option__label, span');
    return candidate?.textContent?.trim() || select.name || 'Product option';
  }

  function nativeHost(select) {
    const wrapper = select.closest('.variant-option__select-wrapper');
    return wrapper || select;
  }

  function getUI(select) {
    const id = select.dataset.bbtSelectUiId;
    return id ? document.getElementById(id) : null;
  }

  function setOpen(ui, open) {
    const trigger = ui.querySelector('.bbt-select-ui__trigger');
    const menu = ui.querySelector('.bbt-select-ui__menu');
    if (!trigger || !menu) return;
    trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
    ui.classList.toggle('is-open', open);
    menu.hidden = !open;
  }

  function closeAll(except = null) {
    document.querySelectorAll('.bbt-select-ui.is-open').forEach((ui) => {
      if (ui !== except) setOpen(ui, false);
    });
  }

  function currentOption(select) {
    return select.options[select.selectedIndex] || select.options[0] || null;
  }

  function buildOptions(select, ui) {
    const menu = ui.querySelector('.bbt-select-ui__menu');
    if (!menu) return;
    menu.textContent = '';

    Array.from(select.options).forEach((option, index) => {
      const item = document.createElement('button');
      item.type = 'button';
      item.className = 'bbt-select-ui__option';
      item.setAttribute('role', 'option');
      item.dataset.index = String(index);
      item.dataset.value = option.value;
      item.textContent = option.textContent || option.label || option.value;
      item.disabled = option.disabled;
      item.setAttribute('aria-disabled', option.disabled ? 'true' : 'false');
      item.setAttribute('aria-selected', option.selected ? 'true' : 'false');

      if (option.selected) item.classList.add('is-selected');
      if (option.disabled) item.classList.add('is-disabled');

      item.addEventListener('click', () => {
        if (item.disabled) return;
        chooseOption(select, Number(item.dataset.index), ui);
      });
      menu.appendChild(item);
    });
  }

  function syncOne(select) {
    const ui = getUI(select);
    if (!ui || !select.isConnected) return;

    const triggerText = ui.querySelector('.bbt-select-ui__value');
    const option = currentOption(select);
    if (triggerText) triggerText.textContent = option?.textContent || option?.label || 'Select an option…';

    const required = select.required || select.dataset.bbtWasRequired === 'true';
    const invalid = required && !select.value;
    ui.classList.toggle('is-invalid', invalid || select.getAttribute('aria-invalid') === 'true');

    const items = ui.querySelectorAll('.bbt-select-ui__option');
    if (items.length !== select.options.length) {
      buildOptions(select, ui);
      return;
    }

    items.forEach((item, index) => {
      const nativeOption = select.options[index];
      if (!nativeOption) return;
      item.textContent = nativeOption.textContent || nativeOption.label || nativeOption.value;
      item.disabled = nativeOption.disabled;
      item.setAttribute('aria-disabled', nativeOption.disabled ? 'true' : 'false');
      item.setAttribute('aria-selected', nativeOption.selected ? 'true' : 'false');
      item.classList.toggle('is-selected', nativeOption.selected);
      item.classList.toggle('is-disabled', nativeOption.disabled);
    });
  }

  function queueSyncAll() {
    if (syncQueued) return;
    syncQueued = true;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        syncQueued = false;
        document.querySelectorAll(`${SELECTOR}[${ENHANCED}="true"]`).forEach(syncOne);
      });
    });
  }

  function resolveCurrentSelect(select, ui = null) {
    if (select?.isConnected) return select;

    const nativeId = ui?.dataset?.nativeSelectId || select?.id || '';
    if (nativeId) {
      const byId = document.getElementById(nativeId);
      if (byId instanceof HTMLSelectElement) return byId;
    }

    const name = ui?.dataset?.nativeSelectName || select?.name || '';
    const handle = ui?.dataset?.productHandle || select?.closest?.('variant-picker')?.dataset?.productHandle || '';

    if (handle && name) {
      const picker = document.querySelector(`variant-picker[data-product-handle="${cssEscape(handle)}"]`);
      const matches = picker ? Array.from(picker.querySelectorAll('select')) : [];
      const byName = matches.find(el => el.name === name);
      if (byName instanceof HTMLSelectElement) return byName;
    }

    if (name) {
      const matches = Array.from(document.querySelectorAll(SELECTOR));
      const byName = matches.find(el => el.name === name && el.isConnected);
      if (byName instanceof HTMLSelectElement) return byName;
    }

    return select;
  }

  function chooseOption(select, index, sourceUi = null) {
    select = resolveCurrentSelect(select, sourceUi);
    if (!(select instanceof HTMLSelectElement) || !select.isConnected) return;

    const option = select.options[index];
    if (!option || option.disabled) return;

    select.selectedIndex = index;
    Array.from(select.options).forEach((opt, i) => {
      if (i === index) opt.setAttribute('selected', 'selected');
      else opt.removeAttribute('selected');
    });

    const ui = getUI(select);
    if (ui) setOpen(ui, false);

    select.dispatchEvent(new Event('input', { bubbles: true }));
    select.dispatchEvent(new Event('change', { bubbles: true }));
    queueSyncAll();
  }

  function moveActive(ui, direction) {
    const items = Array.from(ui.querySelectorAll('.bbt-select-ui__option:not(:disabled)'));
    if (!items.length) return;
    const current = document.activeElement;
    let index = items.indexOf(current);
    if (direction === 'first') index = 0;
    else if (direction === 'last') index = items.length - 1;
    else if (direction === 1) index = index < 0 ? 0 : Math.min(items.length - 1, index + 1);
    else if (direction === -1) index = index < 0 ? items.length - 1 : Math.max(0, index - 1);
    items[index]?.focus();
  }

  function enhance(select) {
    if (!(select instanceof HTMLSelectElement)) return;
    if (select.getAttribute(ENHANCED) === 'true') {
      const existingUi = getUI(select);
      if (existingUi?.isConnected) return;
      select.removeAttribute(ENHANCED);
      delete select.dataset.bbtSelectUiId;
    }
    if (select.multiple || select.size > 1) return;

    const host = nativeHost(select);
    const id = `bbt-select-ui-${++uid}`;
    const label = getLabel(select);

    select.setAttribute(ENHANCED, 'true');
    select.dataset.bbtSelectUiId = id;
    if (select.required) {
      select.dataset.bbtWasRequired = 'true';
      // The custom listbox owns user validation. Leaving HTML required on a visually
      // hidden native select can make Chromium report an unfocusable invalid field.
      select.required = false;
    }

    if (host !== select) host.classList.add('bbt-native-select-host--enhanced');
    select.classList.add('bbt-native-select--enhanced');
    select.tabIndex = -1;
    select.setAttribute('aria-hidden', 'true');

    const ui = document.createElement('div');
    ui.id = id;
    ui.className = 'bbt-select-ui';
    ui.dataset.nativeSelectId = select.id || '';
    ui.dataset.nativeSelectName = select.name || '';
    ui.dataset.productHandle = select.closest('variant-picker')?.dataset?.productHandle || '';

    const trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.className = 'bbt-select-ui__trigger';
    trigger.setAttribute('role', 'combobox');
    trigger.setAttribute('aria-haspopup', 'listbox');
    trigger.setAttribute('aria-expanded', 'false');
    trigger.setAttribute('aria-label', label);

    const value = document.createElement('span');
    value.className = 'bbt-select-ui__value';

    const caret = document.createElement('span');
    caret.className = 'bbt-select-ui__caret';
    caret.setAttribute('aria-hidden', 'true');

    const menu = document.createElement('div');
    const menuId = `${id}-menu`;
    menu.id = menuId;
    menu.className = 'bbt-select-ui__menu';
    menu.setAttribute('role', 'listbox');
    menu.setAttribute('aria-label', label);
    menu.hidden = true;
    trigger.setAttribute('aria-controls', menuId);

    trigger.append(value, caret);
    ui.append(trigger, menu);

    if (host === select) select.insertAdjacentElement('afterend', ui);
    else host.insertAdjacentElement('afterend', ui);

    buildOptions(select, ui);
    syncOne(select);

    trigger.addEventListener('click', () => {
      const opening = !ui.classList.contains('is-open');
      closeAll(opening ? ui : null);
      setOpen(ui, opening);
      if (opening) {
        const currentSelect = resolveCurrentSelect(select, ui);
        buildOptions(currentSelect, ui);
        syncOne(currentSelect);
        const selected = ui.querySelector('.bbt-select-ui__option.is-selected:not(:disabled)');
        selected?.scrollIntoView({ block: 'nearest' });
      }
    });

    trigger.addEventListener('keydown', (event) => {
      if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
        event.preventDefault();
        if (!ui.classList.contains('is-open')) setOpen(ui, true);
        buildOptions(select, ui);
        if (event.key === 'ArrowDown') moveActive(ui, 1);
        if (event.key === 'ArrowUp') moveActive(ui, -1);
        if (event.key === 'Home') moveActive(ui, 'first');
        if (event.key === 'End') moveActive(ui, 'last');
      } else if (event.key === 'Escape') {
        setOpen(ui, false);
      }
    });

    menu.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowDown') { event.preventDefault(); moveActive(ui, 1); }
      if (event.key === 'ArrowUp') { event.preventDefault(); moveActive(ui, -1); }
      if (event.key === 'Home') { event.preventDefault(); moveActive(ui, 'first'); }
      if (event.key === 'End') { event.preventDefault(); moveActive(ui, 'last'); }
      if (event.key === 'Escape') {
        event.preventDefault();
        setOpen(ui, false);
        trigger.focus();
      }
    });

    select.addEventListener('change', () => queueSyncAll());
    select.addEventListener('input', () => queueSyncAll());
  }

  function enhanceAll(root = document) {
    if (root instanceof HTMLSelectElement && root.matches(SELECTOR)) enhance(root);
    root.querySelectorAll?.(SELECTOR).forEach(enhance);
  }

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    if (!event.target.closest('.bbt-select-ui')) closeAll();
  }, true);

  document.addEventListener('change', queueSyncAll, true);
  document.addEventListener('input', queueSyncAll, true);

  const observer = new MutationObserver((mutations) => {
    let shouldSync = false;
    for (const mutation of mutations) {
      if (mutation.type === 'childList') {
        mutation.addedNodes.forEach((node) => {
          if (node instanceof Element) enhanceAll(node);
        });
        shouldSync = true;
      }
      if (mutation.type === 'attributes') shouldSync = true;
    }
    if (shouldSync) queueSyncAll();
  });

  function start() {
    enhanceAll(document);
    observer.observe(document.documentElement, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['selected', 'disabled', 'aria-invalid']
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
